import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import { TableBaseDataEntity } from 'entities/Table';
import { normalizeTableRowEntity, TableRowEntity } from 'entities/TableRow';
import {
  TableBodyRequestApi,
  TableBodyResponseApi,
} from 'entities/endpoints/enum.table_body';
import { ApiRequest } from 'stores/common/ApiRequest';
import { TableCellController } from 'stores/common/TableController';
import { ToggleStateStore } from 'stores/common/ToggleStateStore';
import { ILocalStore } from 'stores/interfaces';

import { ITableFactoryStore } from '../TableFactoryStore/interface';

import { TableFactoryRowUpdateManager } from './TableFactoryRowUpdateManager';
import { isCollapseRowEvent, isFetchMoreRows, RowEvents } from './config';
import { ITableFactoryRowModel } from './interface';
import { CellUpdateParams } from './types';

type PrivateFields =
  | '_entity'
  | '_cells'
  | '_children'
  | '_buildCellControllers';

type VerificationRowStoreParams = {
  entity: TableRowEntity | null;
  parent: TableFactoryRowModel | null;
  formTableStore: ITableFactoryStore;
};

const ROOT_ROW_ID = 'ROOT_ROW_ID';

/**
 * Стор для строки универсальной таблицы
 * */
class TableFactoryRowModel implements ILocalStore, ITableFactoryRowModel {
  constructor({ entity, parent, formTableStore }: VerificationRowStoreParams) {
    this._entity = entity;

    this._parent = parent;
    this.factoryTableStore = formTableStore;

    this.updateManager = new TableFactoryRowUpdateManager({
      tableFactoryStore: formTableStore,
    });

    makeObservable<TableFactoryRowModel, PrivateFields>(this, {
      _entity: observable.ref,
      entity: computed,
      updateEntity: action.bound,

      subTreeRootRecordId: computed,

      handleCallback: action.bound,

      subRowsMap: computed,
      subRows: computed,

      cells: computed,
      _cells: observable.ref,
      _buildCellControllers: action.bound,

      _children: observable.ref,
      children: computed,

      fetchSubtree: action.bound,
    });

    this._buildCellControllers();

    this._disposers.push(
      reaction(
        () => this.expandedState.opened,
        (state) => {
          if (state) {
            /* Стартуем полинг заново, так как обновился список раскрытых строк*/
            this.factoryTableStore.rootRow.restartPolling();
          }
        }
      )
    );
  }

  /** Сущность строки, по которой создаётся стор */
  private _entity: TableRowEntity | null;

  private readonly _disposers: IReactionDisposer[] = [];

  /** Родительский стор */
  readonly factoryTableStore: ITableFactoryStore;

  readonly fetchRowsRequest = new ApiRequest({
    methodType: 'GET',
    endpoint: ENDPOINTS.form.tableData,
  });

  /** Подгруженные поддеревья */
  private _children: TableFactoryRowModel[] = [];

  /** Список ячеек-контроллеров внутри строки */
  private _cells: Record<string, TableCellController> = {};

  /** Ссылка на родительскую модель строки ( null, если строка на верхнем уровне ) */
  private readonly _parent: TableFactoryRowModel | null;

  readonly updateManager: TableFactoryRowUpdateManager;

  /** Флаг, который показывает, раскрыта строка или нет*/
  readonly expandedState = new ToggleStateStore();

  /** Всё дерево дочерних моделей строк, преобразованное в массив */
  get subRowsMap(): Record<string, TableFactoryRowModel> {
    return this._children.reduce<Record<string, TableFactoryRowModel>>(
      (acc, curr) => {
        if (!curr._entity?.rowId) {
          return acc;
        }

        return { ...acc, [curr._entity.rowId]: curr, ...curr.subRowsMap };
      },
      { [ROOT_ROW_ID]: this }
    );
  }

  get subRows(): TableFactoryRowModel[] {
    return Object.values(this.subRowsMap);
  }

  /** Уникальный ID среди всех строк, позволяющий правильно работать маеханизму поддеревьев*/
  get uniqueId(): string {
    if (!this._entity) {
      return String(null);
    }

    return this._entity.rowId;
  }

  get entity() {
    return this._entity;
  }

  get cells() {
    return this._cells;
  }

  get children() {
    return this._children;
  }

  private get _clone(): TableFactoryRowModel {
    return this;
  }

  /** ID корневого элемента
   * Кейсы:
   * 1. Передаём ID подузла, относительно которого запрашиваем дерево, если эта строчка корневая и мы запрашиваем плоскую таблицу относительно узла
   * 2. Передаём ID самой строки, если запрашиваем подузел в сводной таблице
   * 3. Ничего не передаём, если хотим получить всё дерево ( запрашиваем от корневой строки и не находимся в плоской таблице относительно узла )
   * */
  get subTreeRootRecordId(): string | undefined {
    return (
      this._entity?.rowId || this.factoryTableStore.callParams?.root_record_id
    );
  }

  async updateCell(params: CellUpdateParams): Promise<void> {
    await this.updateManager.updateCell(params);
  }

  restartPolling(): void {
    this.updateManager?.start();
  }

  updateEntity(newEntity: TableRowEntity): void {
    let modelToUpdate: TableFactoryRowModel;

    if (newEntity.rowId === this._entity?.rowId) {
      /* Избегаем прямого копирования this, чтобы скипнуть предупреждения от ts компилятора*/
      modelToUpdate = this._clone;
    } else {
      modelToUpdate = this.subRowsMap[newEntity.rowId];
    }

    if (!modelToUpdate) {
      return;
    }

    modelToUpdate._entity = newEntity;

    this.factoryTableStore.columns.forEach((curr) => {
      const newCell = modelToUpdate.entity?.columns[curr.data.id];

      if (!newCell) {
        return;
      }

      if (!modelToUpdate.cells[newCell.fieldId].controller.isUpdateBlocked) {
        modelToUpdate.cells[newCell.fieldId]?.update({
          ...newCell,
          parentRowModel: this,
        });
      }
    });
  }

  /** Создаём модели ячеек внутри текущей строки */
  private _buildCellControllers(): void {
    /* Дестроим старые, если обновляем данные  */
    Object.values(this._cells).forEach((cell) => cell.destroy());

    this._cells = this.factoryTableStore.columns.reduce((acc, curr) => {
      if (!this._entity) {
        return acc;
      }

      const cell = this._entity.columns[curr.data.id];

      if (!cell) {
        return acc;
      }

      return {
        ...acc,
        [cell.fieldId]: new TableCellController({
          ...cell,
          parentRowModel: this,
        }),
      };
    }, {});
  }

  /** Метод подгрузки поддеревьев для сводной таблицы */
  async fetchSubtree(): Promise<TableBaseDataEntity | null> {
    if (!this.factoryTableStore.callParams) {
      return null;
    }

    const requestData = {
      ...this.factoryTableStore.callParams,
      root_record_id: this.subTreeRootRecordId,
    };
    const { data, isError } = await this.fetchRowsRequest.call<
      TableBodyResponseApi,
      TableBodyRequestApi
    >({
      data: requestData,
    });

    if (isError || !data) {
      return null;
    }

    runInAction(() => {
      // Normalize all rows
      const normalizedRows: TableRowEntity[] = data.rows.map((item) =>
        normalizeTableRowEntity(item)
      );

      // Populate the data store for virtualized mode
      this.factoryTableStore.rowDataStore.setRows(normalizedRows);

      // Clear the row model cache (old models should be destroyed)
      this.factoryTableStore.rowModelCache.clear();

      // Skip legacy model creation in virtualized mode
      if (this.factoryTableStore.virtualizedMode) {
        // Just destroy old children, don't create new ones
        this._children.forEach((child) => child.destroy());
        this._children = [];
      } else {
        // Legacy mode: create row models for backward compatibility
        this._children.forEach((child) => child.destroy());
        this._children = normalizedRows.map((entity) => {
          return new TableFactoryRowModel({
            entity,
            formTableStore: this.factoryTableStore,
            parent: this,
          });
        });
      }
    });

    return {
      lastUpdateDate: data.last_update_dt,
      rootRecordEntityId: data.root_record_id,
    };
  }

  handleCallback(event: RowEvents): void {
    if (isCollapseRowEvent(event)) {
      this.expandedState.close();

      return;
    }

    if (isFetchMoreRows(event)) {
      if (this._children.length === 0) {
        this.fetchSubtree().then(() => this.expandedState.open());

        return;
      }

      this.expandedState.open();
    }
  }

  destroy() {
    Object.values(this._cells).forEach((cell) => cell.destroy());
    this._disposers.forEach((reaction) => reaction());
    this.children.forEach((child) => child.destroy());
    this.fetchRowsRequest.destroy();
    this.expandedState.destroy();
    this.updateManager?.destroy();
  }
}

export default TableFactoryRowModel;
